/**
 * LeetCode API — same GraphQL queries used by leetcode-query / LeetCode-Stats-Card
 *
 * Data is live, but each visitor's browser only hits LeetCode **once every
 * three hours**: the payload is cached in localStorage and reused until it is
 * 3 hours old, so the numbers stay current without every page view costing a
 * round trip. Dev uses a 60s window so the page is workable while developing.
 */

const USERNAME = "jugshaurya";
const CACHE_KEY = "lc_data_v5";
const IS_DEV = process.env.NODE_ENV === "development";
const THREE_HOURS = 3 * 60 * 60 * 1000;
const CACHE_TTL = IS_DEV ? 60_000 : THREE_HOURS;

async function gql(query: string, variables: Record<string, any> = {}) {
  const res = await fetch("/api/leetcode", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json?.errors?.length) {
    // Without this a schema change reads as "no user" and silently falls back
    console.warn("LeetCode GraphQL:", json.errors.map((e: any) => e.message).join(" | "));
  }
  return json;
}

/* ── Types ── */
export interface ContestEntry {
  rating: number;
  ranking: number;
  problemsSolved: number;
  totalProblems: number;
  title: string;
  startTime: number;
}

export interface LCData {
  // Profile
  username: string; realName: string; avatar: string; ranking: number;
  country: string; aboutMe: string; website: string; github: string;
  // Solved
  totalSolved: number; easySolved: number; mediumSolved: number; hardSolved: number;
  totalEasy: number; totalMedium: number; totalHard: number; totalAll: number;
  // Contest
  contestRating: number; contestGlobalRank: number; contestTotal: number;
  contestAttended: number; contestTop: number; contestBadge: string;
  contestHistory: ContestEntry[];
  // Badges
  badges: { name: string; icon: string }[];
  // Languages
  languages: { name: string; count: number }[];
  // Skills
  advSkills: { name: string; count: number }[];
  intSkills: { name: string; count: number }[];
  // Calendar (heatmap)
  calendar: Record<string, number>;
  totalSubmissions: number; activeDays: number; maxStreak: number;
  // Community
  reputation: number; contributions: number;
  // Meta
  activeYears: number[];
  fetchedAt: number; isLive: boolean;
}

/* ── Fetch ── */
export async function fetchLC(): Promise<LCData> {
  // Cache check
  try {
    const c = localStorage.getItem(CACHE_KEY);
    if (c) { const p = JSON.parse(c) as LCData; if (Date.now() - p.fetchedAt < CACHE_TTL) return { ...p, isLive: false }; }
  } catch {}

  try {
    // Query 1: User profile + stats (same as leetcode-query's .user() method)
    const q1 = gql(`{
      allQuestionsCount { difficulty count }
      matchedUser(username: "${USERNAME}") {
        username
        profile { realName userAvatar countryName aboutMe websites ranking reputation }
        submitStatsGlobal { acSubmissionNum { difficulty count } }
        badges { displayName icon }
        languageProblemCount { languageName problemsSolved }
        tagProblemCounts {
          advanced { tagName problemsSolved }
          intermediate { tagName problemsSolved }
        }
        contributions { points }
      }
    }`);

    // Query 2: Contest ranking + history (same as leetcode-query's .user_contest_info())
    const q2 = gql(`{
      userContestRanking(username: "${USERNAME}") {
        rating globalRanking totalParticipants attendedContestsCount topPercentage
        badge { name }
      }
      userContestRankingHistory(username: "${USERNAME}") {
        attended problemsSolved totalProblems rating ranking
        contest { title startTime }
      }
    }`);

    // Query 3: which years have any activity at all
    const q3 = gql(`query c($u: String!) {
      matchedUser(username: $u) { userCalendar { activeYears } }
    }`, { u: USERNAME });

    const [r1, r2, r3] = await Promise.all([q1, q2, q3]);

    // Then one calendar per active year, so the heatmap can show them all
    const curYear = new Date().getFullYear();
    const activeYears: number[] = (r3.data?.matchedUser?.userCalendar?.activeYears || [curYear])
      .map(Number).sort((a: number, b: number) => a - b);
    const yearCals = await Promise.all(activeYears.map((y) => gql(`query calendar($username: String!, $year: Int) {
      matchedUser(username: $username) {
        userCalendar(year: $year) { submissionCalendar }
      }
    }`, { username: USERNAME, year: y })));

    const u = r1.data?.matchedUser;
    const aq = r1.data?.allQuestionsCount || [];
    const cr = r2.data?.userContestRanking;
    const ch = (r2.data?.userContestRankingHistory || []).filter((c: any) => c.attended);

    if (!u) throw new Error("no user");

    const subs = u.submitStatsGlobal?.acSubmissionNum || [];
    const f = (d: string) => subs.find((x: any) => x.difficulty === d)?.count || 0;
    const fq = (d: string) => aq.find((x: any) => x.difficulty === d)?.count || 0;

    // Parse and merge every active year's calendar
    let cal: Record<string, number> = {};
    yearCals.forEach((res) => {
      try {
        const str = res.data?.matchedUser?.userCalendar?.submissionCalendar;
        if (str) cal = { ...cal, ...JSON.parse(str) };
      } catch {}
    });

    // The heatmap now shows every active year, so the stats span all of it.
    const ts = Object.keys(cal).map(Number).sort((a, b) => a - b);
    const activeDays = ts.filter(t => cal[String(t)] > 0).length;
    const totalSubs = Object.values(cal).reduce((a, b) => a + (Number(b) || 0), 0);
    let maxStreak = 0, streak = 0;
    for (let i = 0; i < ts.length; i++) {
      if (cal[String(ts[i])] > 0) {
        if (i > 0 && ts[i] - ts[i - 1] <= 86400 * 1.5) streak++; else streak = 1;
        maxStreak = Math.max(maxStreak, streak);
      }
    }

    const data: LCData = {
      username: u.username,
      realName: u.profile?.realName || "Shaurya",
      avatar: u.profile?.userAvatar || "",
      ranking: u.profile?.ranking || 0,
      country: u.profile?.countryName || "India",
      aboutMe: u.profile?.aboutMe || "",
      website: (u.profile?.websites || [])[0] || "",
      github: USERNAME,   // LeetCode no longer exposes profile.gitHub
      totalSolved: f("All"), easySolved: f("Easy"), mediumSolved: f("Medium"), hardSolved: f("Hard"),
      totalEasy: fq("Easy"), totalMedium: fq("Medium"), totalHard: fq("Hard"),
      totalAll: fq("Easy") + fq("Medium") + fq("Hard"),
      contestRating: Math.round(cr?.rating || 2244),
      contestGlobalRank: cr?.globalRanking || 6013,
      contestTotal: cr?.totalParticipants || 855889,
      contestAttended: cr?.attendedContestsCount || 34,
      contestTop: Math.round((cr?.topPercentage || 0.74) * 100) / 100,
      contestBadge: cr?.badge?.name || "Guardian",
      contestHistory: ch.map((c: any) => ({
        rating: Math.round(c.rating * 100) / 100,
        ranking: c.ranking,
        problemsSolved: c.problemsSolved,
        totalProblems: c.totalProblems,
        title: c.contest?.title || "",
        startTime: c.contest?.startTime || 0,
      })),
      badges: (u.badges || []).map((b: any) => ({ name: b.displayName, icon: b.icon || "" })),
      languages: (u.languageProblemCount || []).filter((l: any) => l.problemsSolved > 0)
        .map((l: any) => ({ name: l.languageName, count: l.problemsSolved })),
      advSkills: (u.tagProblemCounts?.advanced || []).slice(0, 6).map((t: any) => ({ name: t.tagName, count: t.problemsSolved })),
      intSkills: (u.tagProblemCounts?.intermediate || []).slice(0, 6).map((t: any) => ({ name: t.tagName, count: t.problemsSolved })),
      calendar: cal,
      totalSubmissions: totalSubs, activeDays, maxStreak,
      reputation: u.profile?.reputation || 0,
      contributions: u.contributions?.points || 0,
      activeYears,
      fetchedAt: Date.now(), isLive: true,
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    return data;
  } catch (e: any) {
    console.error("LC fetch failed:", e?.message || e, e);
    try { const c = localStorage.getItem(CACHE_KEY); if (c) return { ...JSON.parse(c), isLive: false }; } catch {}
    // Hard fallback
    return { username: USERNAME, realName: "Shaurya", avatar: "", ranking: 84378, country: "India", aboutMe: "Hola!", website: "", github: "jugshaurya", totalSolved: 697, easySolved: 121, mediumSolved: 405, hardSolved: 171, totalEasy: 932, totalMedium: 2027, totalHard: 915, totalAll: 3874, contestRating: 2244, contestGlobalRank: 6013, contestTotal: 855889, contestAttended: 34, contestTop: 0.74, contestBadge: "Guardian", contestHistory: [], badges: [{ name: "Guardian", icon: "" }], languages: [{ name: "C++", count: 693 }], advSkills: [{ name: "Dynamic Programming", count: 153 }], intSkills: [], calendar: {}, activeYears: [new Date().getFullYear()], totalSubmissions: 1254, activeDays: 211, maxStreak: 25, reputation: 0, contributions: 0, fetchedAt: 0, isLive: false } as LCData;
  }
}
